/**
 * Mini TV Football 3D sidebar: other 9 matches' scores (port from platform-tv Football3dSidebarScores).
 * Shows non-featured events with live scores; accepts otherEvents and weekResults (or scores map).
 */
import React from 'react';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import { getEventIdForDisplay } from 'features/events/eventId';
import { DEFAULT_FOOTBALL_SCORE } from 'features/video/components/mini-tv/miniTvDefaults';

function toTeam(event) {
  const homeTeam = event?.home_team ?? event?.events_football3d?.home_team;
  const awayTeam = event?.away_team ?? event?.events_football3d?.away_team;
  return {
    homeCode: homeTeam?.code ?? event?.home_team_code ?? 'HOME',
    awayCode: awayTeam?.code ?? event?.away_team_code ?? 'AWAY',
  };
}

export default function MiniTvFootball3dSidebarScores({ otherEvents = [], weekResults = {}, executionTimeline = null }) {
  const list = Array.isArray(otherEvents) ? otherEvents : [];
  if (list.length === 0) {
    return (
      <div className="c7-mini-tv-football-sidebar">
        <div className="c7-mini-tv-football-sidebar__title">Live scores</div>
        <div className="c7-mini-tv-football-sidebar__empty">—</div>
      </div>
    );
  }

  const otherEventIdsKey = React.useMemo(() => list.map((e) => getEventIdForDisplay(e)).join(','), [list]);
  const timelineKey = React.useMemo(() => {
    const startedAtMs = executionTimeline?.startedAtMs ?? null;
    const totalDurationMs = executionTimeline?.totalDurationMs ?? null;
    const cycle = executionTimeline?.eventCycleKey ?? null;
    return `${cycle ?? ''}|${startedAtMs ?? ''}|${totalDurationMs ?? ''}`;
  }, [executionTimeline?.eventCycleKey, executionTimeline?.startedAtMs, executionTimeline?.totalDurationMs]);

  const [scores, setScores] = React.useState({});
  const [flashTokens, setFlashTokens] = React.useState({});
  const appliedOrdersRef = React.useRef({});
  const prevFlashTokensRef = React.useRef({});
  const scheduledTimeoutsRef = React.useRef(new Set());

  // Reset base scores when the set of events changes.
  React.useEffect(() => {
    if (!list.length) {
      if (Object.keys(scores).length > 0) setScores({});
      if (Object.keys(flashTokens).length > 0) setFlashTokens({});
      appliedOrdersRef.current = {};
      prevFlashTokensRef.current = {};
      scheduledTimeoutsRef.current.forEach((h) => clearTimeout(h));
      scheduledTimeoutsRef.current.clear();
      return;
    }
    const next = {};
    const applied = {};
    for (const e of list) {
      const id = getEventIdForDisplay(e);
      if (!id) continue;
      next[id] = { ...DEFAULT_FOOTBALL_SCORE };
      applied[id] = new Set();
    }
    appliedOrdersRef.current = applied;
    setScores(next);
    setFlashTokens({});
    prevFlashTokensRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherEventIdsKey]);

  // Imperative flash: restart CSS animation whenever a token changes.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const prev = prevFlashTokensRef.current || {};
    for (const [eid, token] of Object.entries(flashTokens || {})) {
      if ((prev[eid] ?? 0) === token) continue;
      const el = document.getElementById(`c7-mini-tv-football-sidebar-row-${eid}`);
      if (!el) continue;
      el.classList.remove('c7-mini-tv-football-sidebar-row--flash');
      // Force reflow to allow restarting the animation.
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.classList.add('c7-mini-tv-football-sidebar-row--flash');
      window.setTimeout(() => {
        try {
          el.classList.remove('c7-mini-tv-football-sidebar-row--flash');
        } catch {
          // ignore
        }
      }, 1400);
    }
    prevFlashTokensRef.current = flashTokens;
  }, [flashTokens]);

  React.useEffect(() => {
    // Clear any previously scheduled updates whenever we reschedule.
    scheduledTimeoutsRef.current.forEach((h) => clearTimeout(h));
    scheduledTimeoutsRef.current.clear();

    if (!list.length) return;
    if (!weekResults || typeof weekResults !== 'object') return;

    const startedAtMs = executionTimeline?.startedAtMs ?? null;
    const totalDurationMs = executionTimeline?.totalDurationMs ?? null;
    if (startedAtMs == null || totalDurationMs == null) return;
    if (!Number.isFinite(totalDurationMs) || totalDurationMs <= 0) return;

    const now = Date.now();
    const elapsed = Math.max(0, now - startedAtMs);
    const ratio = Math.min(1, elapsed / totalDurationMs);
    const virtualMinuteNow = Math.max(0, Math.min(90, Math.floor(ratio * 90)));

    const computeInitialScores = () => {
      const initialScores = {};
      const applied = {};

      for (const e of list) {
        const eid = getEventIdForDisplay(e);
        if (!eid) continue;
        initialScores[eid] = { ...DEFAULT_FOOTBALL_SCORE };
        applied[eid] = new Set();

        const res = weekResults?.[eid];
        const actions = Array.isArray(res?.actions) ? res.actions : [];
        const goals = actions
          .map((a) => {
            const m = a?.minute != null ? Number(a.minute) : null;
            const order = a?.sequence_order != null ? Number(a.sequence_order) : null;
            const type = a?.action_type ? String(a.action_type) : null;
            const scoringTeam = a?.scoring_team ? String(a.scoring_team) : null;
            if (m == null || order == null) return null;
            if (type !== 'GOL') return null;
            if (scoringTeam !== 'HOME' && scoringTeam !== 'AWAY') return null;
            return { minute: m, sequence_order: order, scoring_team: scoringTeam };
          })
          .filter(Boolean);

        goals
          .filter((g) => g.minute <= virtualMinuteNow)
          .sort((a, b) => (a.minute - b.minute) || (a.sequence_order - b.sequence_order))
          .forEach((g) => {
            applied[eid].add(g.sequence_order);
            if (g.scoring_team === 'HOME') initialScores[eid].home += 1;
            if (g.scoring_team === 'AWAY') initialScores[eid].away += 1;
          });
      }

      return { initialScores, applied };
    };

    const { initialScores, applied } = computeInitialScores();
    appliedOrdersRef.current = applied;
    setScores(initialScores);
    setFlashTokens({});
    prevFlashTokensRef.current = {};

    const applyGoal = (eid, goal, flash) => {
      const appliedSet = appliedOrdersRef.current?.[eid] || (appliedOrdersRef.current[eid] = new Set());
      if (appliedSet.has(goal.sequence_order)) return;
      appliedSet.add(goal.sequence_order);

      setScores((prev) => {
        const cur = prev?.[eid] || { ...DEFAULT_FOOTBALL_SCORE };
        const nextScore =
          goal.scoring_team === 'HOME'
            ? { home: cur.home + 1, away: cur.away }
            : { home: cur.home, away: cur.away + 1 };
        return { ...prev, [eid]: nextScore };
      });

      if (flash) {
        setFlashTokens((prev) => ({ ...prev, [eid]: (prev?.[eid] ?? 0) + 1 }));
      }
    };

    for (const e of list) {
      const eid = getEventIdForDisplay(e);
      if (!eid) continue;
      const res = weekResults?.[eid];
      const actions = Array.isArray(res?.actions) ? res.actions : [];
      const goals = actions
        .map((a) => {
          const m = a?.minute != null ? Number(a.minute) : null;
          const order = a?.sequence_order != null ? Number(a.sequence_order) : null;
          const type = a?.action_type ? String(a.action_type) : null;
          const scoringTeam = a?.scoring_team ? String(a.scoring_team) : null;
          if (m == null || order == null) return null;
          if (type !== 'GOL') return null;
          if (scoringTeam !== 'HOME' && scoringTeam !== 'AWAY') return null;
          return { minute: m, sequence_order: order, scoring_team: scoringTeam };
        })
        .filter(Boolean)
        .sort((a, b) => (a.minute - b.minute) || (a.sequence_order - b.sequence_order));

      goals.forEach((g) => {
        if (appliedOrdersRef.current?.[eid]?.has(g.sequence_order)) return;
        const scheduledAt = startedAtMs + Math.round((g.minute / 90) * totalDurationMs);
        const delay = scheduledAt - Date.now();
        if (delay <= 0) {
          applyGoal(eid, g, false);
          return;
        }
        const h = window.setTimeout(() => applyGoal(eid, g, true), delay);
        scheduledTimeoutsRef.current.add(h);
      });
    }

    return () => {
      scheduledTimeoutsRef.current.forEach((h) => clearTimeout(h));
      scheduledTimeoutsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineKey, otherEventIdsKey, weekResults]);

  return (
    <div className="c7-mini-tv-football-sidebar">
      <div className="c7-mini-tv-football-sidebar__title">Live scores</div>
      <div className="c7-mini-tv-football-sidebar__rows">
        {list.map((e) => {
          const id = getEventIdForDisplay(e);
          const s = scores?.[id] ?? { ...DEFAULT_FOOTBALL_SCORE };
          const { homeCode, awayCode } = toTeam(e);
          return (
            <div
              id={`c7-mini-tv-football-sidebar-row-${id}`}
              key={id}
              className="c7-mini-tv-football-sidebar-row"
            >
              <div className="c7-mini-tv-football-sidebar-row__home">
                <img
                  src={getFootball3dTeamLogoUrl(homeCode)}
                  alt={homeCode}
                  className="c7-mini-tv-football-sidebar-row__logo"
                />
                <span className="c7-mini-tv-football-sidebar-row__code">{homeCode}</span>
              </div>
              <div className="c7-mini-tv-football-sidebar-row__score">
                {s.home}-{s.away}
              </div>
              <div className="c7-mini-tv-football-sidebar-row__id">ID: {id}</div>
              <div className="c7-mini-tv-football-sidebar-row__away">
                <img
                  src={getFootball3dTeamLogoUrl(awayCode)}
                  alt={awayCode}
                  className="c7-mini-tv-football-sidebar-row__logo"
                />
                <span className="c7-mini-tv-football-sidebar-row__code">{awayCode}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
